import { Grid, useEventCallback, SelectChangeEvent } from '@mui/material';
import { memo } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import RequestedList from './requested-list';
import AssumeList from './assume-list';
import { EditDataControl } from 'subscription/view-models/common-interface-subscription';
import { FormikErrors, FormikTouched } from 'formik';
import SubscribeToList from './subscribe-to-list';
import PmiDatePicker from 'shell/components/shared/date-picker';
import FlagValidationFiscalCode from 'shell/components/shared/flag-validation-fiscal-code/flag';

interface FormEditControlDataProps {
  values?: EditDataControl;
  onChange: (key: keyof EditDataControl, value: EditDataControl[keyof EditDataControl]) => void;
  errors?: FormikErrors<EditDataControl>;
  touched?: FormikTouched<EditDataControl>;
  isCohabitation: boolean;
  isAire?: boolean;
  disabledFromAct?: boolean;
}

const FormEditControlDataComponent = ({
  values,
  onChange,
  errors,
  touched,
  isCohabitation = false,
  isAire = false,
  disabledFromAct = false,
}: FormEditControlDataProps): React.ReactElement => {
  const handleFlagChange = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('flag', event.target.value);
  });

  const handleRequestedChange = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('requested', event.target.value);
  });

  const handleAssumeChange = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('assume', event.target.value);
  });

  const handleSubscribeToChange = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('subscribeTo', event.target.value);
  });

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={4}>
        <PmiDatePicker
          label="Data decorrenza *"
          value={values?.dateValidity || null}
          onChange={(dataValue) => {
            onChange('dateValidity', dataValue || null);
          }}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="dateValidity"
              fullWidth
              error={touched?.dateValidity && Boolean(errors?.dateValidity)}
              helperText={errors?.dateValidity}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <FlagValidationFiscalCode
          required
          value={values?.flag || ''}
          onChange={handleFlagChange}
          error={touched?.flag && Boolean(errors?.flag)}
        />
      </Grid>
      <Grid
        container
        item
        spacing={0}
        rowSpacing={{ xs: 1, md: 0 }}
        columnSpacing={{ xs: 0, md: 1 }}
        xs={12}
      >
        <Grid item xs={12} md={4}>
          <SubscribeToList
            required
            isCohabitation={isCohabitation}
            value={values?.subscribeTo || ''}
            onChange={handleSubscribeToChange}
            error={touched?.subscribeTo && Boolean(errors?.subscribeTo)}
            disabled={isAire}
          />
        </Grid>
        {isAire ? undefined : (
          <Grid item xs={12} md={4}>
            <RequestedList
              required
              value={values?.requested?.toString() || ''}
              onChange={handleRequestedChange}
              error={touched?.requested && Boolean(errors?.requested)}
              disabled={disabledFromAct}
            />
          </Grid>
        )}
        {isAire ? undefined : (
          <Grid item xs={12} md={4}>
            <AssumeList
              required
              value={values?.assume || ''}
              onChange={handleAssumeChange}
              error={touched?.assume && Boolean(errors?.assume)}
              disabled={disabledFromAct}
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

FormEditControlDataComponent.displayName = 'Form Edit Control data component';

const FormEditControlData = memo(FormEditControlDataComponent);

export default FormEditControlData;
